import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const AssetSelector = ({ selectedAsset, onAssetChange, selectedContract, onContractChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const assetOptions = [
    { 
      value: 'EUA', 
      label: 'EUA (European Union Allowances)',
      description: 'Carbon emission allowances',
      currentPrice: '€85.42',
      change: '+2.34%',
      changeType: 'positive'
    },
    { 
      value: 'CER', 
      label: 'CER (Certified Emission Reductions)',
      description: 'Clean Development Mechanism credits',
      currentPrice: '€12.78',
      change: '-0.89%',
      changeType: 'negative'
    },
    { 
      value: 'CRUDE', 
      label: 'Crude Oil Futures',
      description: 'WTI Crude Oil',
      currentPrice: '$78.25',
      change: '+1.56%',
      changeType: 'positive'
    },
    { 
      value: 'BRENT', 
      label: 'Brent Oil Futures',
      description: 'Brent Crude Oil',
      currentPrice: '$82.10',
      change: '+1.23%',
      changeType: 'positive'
    },
    { 
      value: 'NATGAS', 
      label: 'Natural Gas Futures',
      description: 'Henry Hub Natural Gas',
      currentPrice: '$3.45',
      change: '-2.10%',
      changeType: 'negative'
    }
  ];

  const contractOptions = {
    EUA: [
      { value: 'DEC24', label: 'December 2024' },
      { value: 'MAR25', label: 'March 2025' },
      { value: 'JUN25', label: 'June 2025' },
      { value: 'SEP25', label: 'September 2025' },
      { value: 'DEC25', label: 'December 2025' }
    ],
    CER: [
      { value: 'DEC24', label: 'December 2024' },
      { value: 'MAR25', label: 'March 2025' },
      { value: 'JUN25', label: 'June 2025' }
    ],
    CRUDE: [
      { value: 'JAN25', label: 'January 2025' },
      { value: 'FEB25', label: 'February 2025' },
      { value: 'MAR25', label: 'March 2025' },
      { value: 'APR25', label: 'April 2025' },
      { value: 'MAY25', label: 'May 2025' }
    ],
    BRENT: [
      { value: 'JAN25', label: 'January 2025' },
      { value: 'FEB25', label: 'February 2025' },
      { value: 'MAR25', label: 'March 2025' },
      { value: 'APR25', label: 'April 2025' },
      { value: 'MAY25', label: 'May 2025' }
    ],
    NATGAS: [
      { value: 'JAN25', label: 'January 2025' },
      { value: 'FEB25', label: 'February 2025' },
      { value: 'MAR25', label: 'March 2025' },
      { value: 'APR25', label: 'April 2025' }
    ]
  };

  const currentAsset = assetOptions?.find(asset => asset?.value === selectedAsset);
  const availableContracts = contractOptions?.[selectedAsset] || [];

  const getChangeColor = (changeType) => {
    return changeType === 'positive' ? 'text-success' : 'text-error';
  };

  const getChangeIcon = (changeType) => {
    return changeType === 'positive' ? 'TrendingUp' : 'TrendingDown';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Asset Selection</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
          iconSize={16}
        >
          {isExpanded ? 'Collapse' : 'Expand'}
        </Button>
      </div>
      {/* Compact View */}
      {!isExpanded && (
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-3">
            <Select
              options={assetOptions?.map(asset => ({ value: asset?.value, label: asset?.label }))}
              value={selectedAsset}
              onChange={onAssetChange}
              className="w-64"
            />
            
            {currentAsset && (
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold text-foreground">
                  {currentAsset?.currentPrice}
                </span>
                <span className={`flex items-center gap-1 text-sm font-medium ${getChangeColor(currentAsset?.changeType)}`}>
                  <Icon name={getChangeIcon(currentAsset?.changeType)} size={14} />
                  {currentAsset?.change}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Contract:</span>
            <Select
              options={availableContracts}
              value={selectedContract}
              onChange={onContractChange}
              className="w-40"
            />
          </div>
        </div>
      )}
      {/* Expanded View */}
      {isExpanded && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {assetOptions?.map(asset => (
              <div
                key={asset?.value}
                className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 hover:shadow-subtle ${
                  selectedAsset === asset?.value ? 'border-primary bg-primary/5' : 'border-border'
                }`}
                onClick={() => onAssetChange(asset?.value)}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-foreground">{asset?.value}</h4>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-foreground">
                      {asset?.currentPrice}
                    </div>
                    <div className={`flex items-center gap-1 text-sm font-medium ${getChangeColor(asset?.changeType)}`}>
                      <Icon name={getChangeIcon(asset?.changeType)} size={12} />
                      {asset?.change}
                    </div>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-2">{asset?.label}</p>
                <p className="text-xs text-muted-foreground">{asset?.description}</p>
              </div>
            ))}
          </div>

          {/* Contract Selection */}
          <div className="border-t border-border pt-4">
            <h4 className="font-medium text-foreground mb-3">Available Contracts</h4>
            <div className="flex flex-wrap gap-2">
              {availableContracts?.map(contract => (
                <Button
                  key={contract?.value}
                  variant={selectedContract === contract?.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => onContractChange(contract?.value)}
                  className="text-xs"
                >
                  {contract?.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Market Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-border">
            <div className="p-3 bg-muted rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="Clock" size={14} className="text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">Market Status</span>
              </div>
              <span className="text-sm text-success">Open</span>
            </div>
            
            <div className="p-3 bg-muted rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="BarChart3" size={14} className="text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">Volume (24h)</span>
              </div>
              <span className="text-sm text-foreground">2.4M contracts</span>
            </div>
            
            <div className="p-3 bg-muted rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="Activity" size={14} className="text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">Volatility</span>
              </div>
              <span className="text-sm text-foreground">15.2%</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetSelector;